import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import {
  formatDateOnly, todayInZone, isoDate, dayOfWeek, daysInMonth,
  addMonths, dateParts, withinRange, MONTH_NAMES
} from '../utils/dateOnly';
import './WhosOut.css';

/**
 * Who's Out (TO1c — R-TO7, owner rulings 8, 9, 10, 11).
 *
 * The full calendar the owner asked for in ruling 8: a month grid of WHO is
 * off, gated on time_off:view, so drivers (create only) never reach it.
 *
 * RULING 9 IS THE LOAD-BEARING ONE: this page shows WHO, never WHY. There is
 * no leave type on it for ANY role, approvers included — an approver reads the
 * reason on Attendance, where the decision is made. The feed already strips
 * the type server-side for non-approvers; this page simply never renders it.
 *
 * RULING 10: one colour dimension only — the DEPARTMENT. time_off_types.color
 * is deliberately unused here.
 *
 * RULING 11: blackout ranges are shaded across the days they cover with the
 * reason in the day header. They are information, not a barrier — nothing on
 * this page or behind it refuses anything.
 *
 * DATES: every value handled here is date-only. Nothing constructs a Date from
 * a 'YYYY-MM-DD' string (TO1b.2) — the month grid is integer civil arithmetic
 * from utils/dateOnly, and range tests are lexicographic string compares, so
 * the page renders identically in every viewer timezone.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_BARS = 4;
const UNASSIGNED = 'Unassigned';

const WhosOut = () => {
  const today = todayInZone();
  const todayParts = dateParts(today) || { y: 2026, m: 1, d: 1 };

  const [cursor, setCursor] = useState({ y: todayParts.y, m: todayParts.m });
  const [absences, setAbsences] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);

  const monthStart = isoDate(cursor.y, cursor.m, 1);
  const monthEnd = isoDate(cursor.y, cursor.m, daysInMonth(cursor.y, cursor.m));

  const fetchMonth = useCallback(async () => {
    try {
      setLoading(true);
      const [out, black, depts] = await Promise.all([
        api.get(`/time-off/out-range?start_date=${monthStart}&end_date=${monthEnd}`)
          .then(res => res.data.absences || []).catch(() => []),
        api.get('/time-off/blackouts').then(res => res.data.blackouts || []).catch(() => []),
        api.get('/departments').then(res => res.data.departments || []).catch(() => [])
      ]);
      setAbsences(out);
      setBlackouts(black);
      setDepartments(depts);
    } finally {
      setLoading(false);
    }
  }, [monthStart, monthEnd]);

  useEffect(() => { fetchMonth(); }, [fetchMonth]);

  // Colour by department NAME, because users.department is free text matched to
  // departments.name the way the resolver matches it (R-TO4). The feed already
  // carries the joined colour; the legend map is the fallback for a row whose
  // department text no longer matches a live department.
  const colorFor = useMemo(() => {
    const map = {};
    departments.forEach(d => { map[String(d.name).trim().toLowerCase()] = d.color; });
    return (row) => row.department_color
      || map[String(row.department_name || row.department || '').trim().toLowerCase()]
      || '#6B7280';
  }, [departments]);

  const cells = useMemo(() => {
    const lead = dayOfWeek(cursor.y, cursor.m, 1);
    const total = daysInMonth(cursor.y, cursor.m);
    const out = [];
    for (let i = 0; i < lead; i++) out.push(null);
    for (let d = 1; d <= total; d++) {
      const date = isoDate(cursor.y, cursor.m, d);
      const dow = dayOfWeek(cursor.y, cursor.m, d);
      const onThisDay = absences
        .filter(a => withinRange(date, a.start_date, a.end_date))
        .map(a => ({
          ...a,
          // A half-day flag applies only to the end of the range it belongs to.
          half: (a.start_half_day && date === a.start_date) ||
                (a.end_half_day && date === a.end_date)
        }))
        .sort((x, y) => String(x.employee_name || '').localeCompare(String(y.employee_name || '')));
      out.push({
        date, day: d, dow,
        isToday: date === today,
        isWeekend: dow === 0 || dow === 6,
        people: onThisDay,
        blackouts: blackouts.filter(b => withinRange(date, b.start_date, b.end_date))
      });
    }
    return out;
  }, [cursor, absences, blackouts, today]);

  const selected = useMemo(
    () => cells.find(c => c && c.date === selectedDay) || null,
    [cells, selectedDay]
  );

  const grouped = useMemo(() => {
    if (!selected) return [];
    const byDept = new Map();
    selected.people.forEach(p => {
      const name = (p.department_name || p.department || UNASSIGNED).trim() || UNASSIGNED;
      if (!byDept.has(name)) byDept.set(name, { name, color: colorFor(p), people: [] });
      byDept.get(name).people.push(p);
    });
    return [...byDept.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [selected, colorFor]);

  const step = (delta) => {
    setSelectedDay(null);
    setCursor(prev => addMonths(prev.y, prev.m, delta));
  };

  return (
    <div className="whosout-page">
      <div className="whosout-header">
        <h1>Who's Out</h1>
        <p>Approved time off across the company, coloured by department.</p>
      </div>

      <div className="whosout-toolbar">
        <div className="whosout-month">
          <button className="whosout-nav-btn" onClick={() => step(-1)} aria-label="Previous month">‹</button>
          <h2>{MONTH_NAMES[cursor.m - 1]} {cursor.y}</h2>
          <button className="whosout-nav-btn" onClick={() => step(1)} aria-label="Next month">›</button>
          <button
            className="whosout-nav-btn"
            onClick={() => { setSelectedDay(null); setCursor({ y: todayParts.y, m: todayParts.m }); }}
          >Today</button>
        </div>
        <div className="whosout-legend">
          {departments.map(d => (
            <span key={d.id} className="whosout-legend-item">
              <span className="whosout-legend-swatch" style={{ background: d.color }} />
              {d.name}
            </span>
          ))}
        </div>
      </div>

      <div className="whosout-body">
        <div className="whosout-grid">
          <div className="whosout-weekdays">
            {WEEKDAYS.map(w => <div key={w} className="whosout-weekday">{w}</div>)}
          </div>
          <div className="whosout-days">
            {cells.map((cell, i) => {
              if (!cell) return <div key={`blank-${i}`} className="whosout-day is-blank" />;
              const classes = ['whosout-day'];
              if (cell.isToday) classes.push('is-today');
              if (cell.isWeekend) classes.push('is-weekend');
              if (cell.blackouts.length > 0) classes.push('is-blackout');
              if (selectedDay === cell.date) classes.push('is-selected');
              return (
                <button
                  key={cell.date}
                  type="button"
                  className={classes.join(' ')}
                  onClick={() => setSelectedDay(cell.date)}
                >
                  <div className="whosout-day-header">
                    <span className="whosout-day-number">{cell.day}</span>
                    {cell.blackouts.length > 0 && (
                      <span className="whosout-day-blackout">
                        {cell.blackouts.map(b => b.reason).join(' · ')}
                      </span>
                    )}
                  </div>
                  <div className="whosout-bars">
                    {cell.people.slice(0, MAX_BARS).map(p => (
                      <span
                        key={`${cell.date}-${p.request_id}`}
                        className={`whosout-bar${p.half ? ' is-half' : ''}`}
                        style={{ background: colorFor(p) }}
                        title={`${p.employee_name} — ${p.department_name || p.department || UNASSIGNED}`}
                      >{p.employee_name}</span>
                    ))}
                    {cell.people.length > MAX_BARS && (
                      <span className="whosout-more">+{cell.people.length - MAX_BARS} more</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="whosout-side">
          {!selected ? (
            <>
              <h3>Pick a day</h3>
              <div className="whosout-side-date">
                {loading ? 'Loading…' : 'Click any day to see who is out, grouped by department.'}
              </div>
            </>
          ) : (
            <>
              <h3>Out on this day</h3>
              <div className="whosout-side-date">{formatDateOnly(selected.date)}</div>
              {selected.blackouts.map((b, i) => (
                <div key={`bl-${i}`} className="whosout-side-blackout">
                  <strong>Blackout:</strong> {b.reason}
                  <div>{formatDateOnly(b.start_date)} – {formatDateOnly(b.end_date)}</div>
                </div>
              ))}
              {grouped.length === 0 ? (
                <div className="whosout-empty">Nobody is out.</div>
              ) : grouped.map(g => (
                <div key={g.name} className="whosout-side-group">
                  <div className="whosout-side-group-title">
                    <span className="whosout-legend-swatch" style={{ background: g.color }} />
                    {g.name}
                  </div>
                  {g.people.map(p => (
                    <div key={p.request_id} className="whosout-side-name">
                      {p.employee_name}{p.half ? ' (half day)' : ''}
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhosOut;
